<template>
    <div class="container">
      <div class="header-container">
        <h1>MySQL转Golang Struct</h1>
        <p>复制MySql的建表s语句，自动转化为Go的Struct结构体，支持GORM/JSON/DB/Form类型</p>
      </div>
  
      <div class="areas-container">
        <div class="input-area">
          <el-alert v-if="error" :title="error" type="error" show-icon class="error-alert"/>
          <div class="control-group">
            <el-checkbox-group v-model="selectedTags">
              <el-checkbox label="gorm">GORM</el-checkbox>
              <el-checkbox label="json">JSON</el-checkbox>
              <el-checkbox label="db">DB</el-checkbox>
              <el-checkbox label="form">Form</el-checkbox>
            </el-checkbox-group>
            <el-button 
              type="primary" 
              @click="convertSQL"
              :disabled="!sqlContent"
              class="convert-btn"
            >开始转换</el-button>
          </div>
          
          <el-input
            type="textarea"
            v-model="sqlContent"
            placeholder="粘贴MySQL建表语句..."
            :rows="33"
            resize="none"
            class="sql-input"
          />
        </div>
  
        <div class="output-area">
          <div class="output-header">
            <span>生成结果</span>
            <el-button 
              type="success" 
              plain 
              @click="copyToClipboard"
              :disabled="!golangStruct"
              class="copy-btn"
            >
              <i class="el-icon-document-copy"></i> 一键复制
            </el-button>
          </div>
          <!-- 添加代码高亮容器 -->
          <pre class="struct-output"><code class="language-go">{{ golangStruct || "// 转换结果将在此处显示..." }}</code></pre>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  // 改为更简洁的自动加载方式
  import hljs from 'highlight.js';
  import 'highlight.js/styles/vs2015.css';

  // 自动初始化（会包含所有语言）
  hljs.configure({
    languages: ['go']
  });

  // 类型映射表
  const typeMapping = {
    'int': 'int',
    'tinyint': 'int',
    'smallint': 'int',
    'mediumint': 'int',
    'bigint': 'int64',
    'decimal': 'float64',
    'float': 'float32',
    'double': 'float64',
    'char': 'string',
    'varchar': 'string',
    'text': 'string',
    'date': 'time.Time',
    'datetime': 'time.Time',
    'timestamp': 'time.Time',
    'boolean': 'bool'
  };
  
  export default {
    name: 'SqlToStruct',
    metaInfo: {
      title: "一键SQL转GO Struct", // 设置页面标题
      meta: [
        { name: "description", content: "复制MySql的建表s语句，自动转化为Go的Struct结构体，支持GORM/JSON/DB/Form类型" } // 设置页面描述
      ]
    },
    data() {
      return {
        sqlContent: '',
        golangStruct: '',
        selectedTags: ['gorm', 'json'],
        error: null
      };
    },
    methods: {
      convertSQL() {
        try {
          this.error = null;
          const structData = this.parseSQL(this.sqlContent);
          this.golangStruct = this.generateStruct(structData);

          // 新增代码高亮逻辑
          this.$nextTick(() => {
            document.querySelectorAll('.struct-output code').forEach(el => {
              hljs.highlightElement(el);
            });
          });

        } catch (err) {
          this.error = `转换失败: ${err.message}`;
          this.golangStruct = '';
        }
      },
  
      parseSQL(sql) {
        // 统一转为小写并去除前后空格
        const cleanedSql = sql.toLowerCase().trim();
        console.log(cleanedSql);
  
        // 匹配表名和字段定义
        const tableMatch = cleanedSql.match(/create table (\w+)\s*\(\s*([\s\S]+?)\s*$/i);
        if (!tableMatch) {
            const hasTableKeyword = cleanedSql.includes('create table');
            const hasParentheses = cleanedSql.includes('(') && cleanedSql.includes(')');
            
            if (!hasTableKeyword) throw new Error("缺少CREATE TABLE关键字");
            if (!hasParentheses) throw new Error("字段定义缺少括号");
            throw new Error("无法解析的建表语句结构");
        }
  
        const tableName = tableMatch[1];
        const fields = [];
        
        // 提取字段信息
        const fieldLines = tableMatch[2].split('\n')
          .map(line => line.trim())
          .filter(line => line && !line.startsWith('constraint') && !line.startsWith('primary key'));
  
        for (const line of fieldLines) {
          const fieldMatch = line.match(/(\w+)\s+([\w()]+)\s*(.*?)(comment\s+['"](.*?)['"])?/i);
          if (!fieldMatch) continue;
  
          const [, name, typeStr, modifiers, , comment] = fieldMatch;
          const isPrimaryKey = modifiers.includes('primary key');
          const isAutoIncrement = modifiers.includes('auto_increment');
          
          // 转换类型
          const baseType = typeStr.replace(/$\d+$/, '').toLowerCase();
          const goType = typeMapping[baseType] || 'string';
  
          fields.push({
            name: this.pascalCase(name),
            type: goType,
            comment: comment || '',
            isPrimaryKey,
            isAutoIncrement,
            originalName: name
          });
        }
  
        return { tableName, fields };
      },
  
      generateStruct({ tableName, fields }) {
        let struct = `type ${this.pascalCase(tableName)} struct {\n`;
        
        fields.forEach(field => {
          // 生成标签
          const tags = [];
          if (this.selectedTags.includes('gorm')) {
            let gormTag = `column:${field.originalName}`;
            if (field.isPrimaryKey) gormTag += ';primaryKey';
            if (field.isAutoIncrement) gormTag += ';autoIncrement';
            tags.push(`gorm:"${gormTag}"`);
          }
          if (this.selectedTags.includes('json')) tags.push(`json:"${field.originalName}"`);
          if (this.selectedTags.includes('db')) tags.push(`db:"${field.originalName}"`);
          if (this.selectedTags.includes('form')) tags.push(`form:"${field.originalName}"`);
  
          // 生成字段行
          const tagStr = tags.length > 0 ? ` \`${tags.join(' ')}\`` : '';
          const comment = field.comment ? `  // ${field.comment}` : '';
          struct += `\t${field.name} ${field.type}${tagStr}${comment}\n`;
        });
  
        struct += `}\n\n`;
        struct += `func (${this.pascalCase(tableName)}) TableName() string {\n\treturn "${tableName}"\n}`;
        return struct;
      },
  
      async copyToClipboard() {
        try {
          await navigator.clipboard.writeText(this.golangStruct);
          this.$message.success('复制成功！');
        } catch (err) {
          this.$message.error('复制失败，请手动选择文本复制');
        }
      },
  
      camelCase(str) {
      return str.split('_')
        .map((word, i) => i === 0 
          ? word.toLowerCase() 
          : word[0].toUpperCase() + word.slice(1).toLowerCase())
        .join('');
    },
  
      pascalCase(str) {
        return this.camelCase(str)
          .replace(/^\w/, char => char.toUpperCase());
      },
    }
  };
  </script>
  
  <style scoped>
  .container {
    display: flex;
    flex-direction: column;
    /* height: 100vh; */
    height: calc(100% - 120px);

    padding: 20px;
    background: #f5f7fa;
  }
  
  .header-container {
    text-align: center;
    margin-bottom: 30px;
  }
  
  .header-container h1 {
    color: #2c3e50;
    font-size: 28px;
    margin-bottom: 10px;
  }
  
  .areas-container {
    display: flex;
    flex: 1;
    gap: 20px;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
  }
  
  .input-area, .output-area {
    flex: 1;
    min-width: 0;
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  }
  
  .control-group {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }
  
  .sql-input {
    height: calc(100% - 80px);
    font-family: 'Courier New', monospace;
    font-size: 14px;
  }
  
  .struct-output {
    flex: 1;
    height: calc(100% - 80px);
    background: #1E1E1E !important;
    color: #DCDCDC;
    padding: 15px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
    overflow: auto;
    white-space: pre-wrap;
    font-family: 'Fira Code', monospace;
    font-size: 12px;
    text-align: left;
    line-height: 1.5;
  }

  /* 覆盖highlight.js默认样式 */
  .struct-output code {
    background: transparent !important;
    padding: 0 !important;
  }

  /* 优化代码块显示 */
  .language-go {
    display: block;
    overflow-x: auto;
    padding: 1em;
  }
  
  .output-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }
  
  .copy-btn {
    padding: 8px 15px;
  }
  
  .error-alert {
    margin-bottom: 15px;
  }
  
  @media (max-width: 768px) {
    .areas-container {
      flex-direction: column;
      height: auto;
    }
  
    .input-area, .output-area {
      width: 100%;
      min-height: 400px;
    }
  
    .control-group {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
  }
  </style>


// WEBPACK FOOTER //
// src/components/SqlToGoStruct.vue